<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="propositionForm" @submit="editProposition" :validation-schema="propositionSchema">
        <div class="row">

          <!-- Contenu -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Contenu <span class="text-danger">*</span>
              </label>
              <Field
                name="contenu"
                type="text"
                class="form-control shadow-none fs-md-15 text-black"
                placeholder="Entrer le contenu"
              />
              <ErrorMessage name="contenu" class="text-danger" />
            </div>
          </div>

          <!-- Est la bonne réponse -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Est la bonne réponse <span class="text-danger">*</span>
              </label>
              <Field name="estLaBonneReponse" v-slot="{ field }">
                <Multiselect
                  v-model="field.value"
                  :options="booleanOptions"
                  :searchable="false"
                  placeholder="Sélectionner"
                  label="label"
                  track-by="value"
                />
              </Field>
              <ErrorMessage name="estLaBonneReponse" class="text-danger" />
            </div>
          </div>

          <!-- Question -->
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Question <span class="text-danger">*</span>
              </label>
              <Field name="questionId" v-slot="{ field }">
                <Multiselect
                  v-model="field.value"
                  :options="questionOptions"
                  :searchable="true"
                  placeholder="Sélectionner une question"
                  label="label"
                  track-by="value"
                  :loading="loadingQuestions"
                />
              </Field>
              <ErrorMessage name="questionId" class="text-danger" />
            </div>
          </div>

          <!-- Boutons -->
          <div class="col-md-12 mt-4">
            <div class="d-flex align-items-center gap-3">
              <button
                type="submit"
                class="default-btn transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 rounded-1 fs-md-15 bg-success"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                Modifier la proposition
              </button>
              <router-link to="/liste-propositions" class="btn btn-danger">
                Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, nextTick } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import { useRoute, useRouter } from 'vue-router';
import ApiService from '@/services/ApiService';
import { error } from '@/utils/utils';
import Multiselect from '@vueform/multiselect';
import Swal from 'sweetalert2';

export default defineComponent({
  name: 'EditPropositionReponse',
  components: { Form, Field, ErrorMessage, Multiselect },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const propositionForm = ref<InstanceType<typeof Form> | null>(null);
    const isSubmitting = ref(false);
    const loadingQuestions = ref(false);
    const id = route.params.id as string;

    const propositionSchema = Yup.object().shape({
      contenu: Yup.string().required('Le contenu est obligatoire'),
      estLaBonneReponse: Yup.boolean().required('Ce champ est obligatoire'),
      questionId: Yup.number().required('La question est obligatoire').positive().integer(),
    });

    const booleanOptions = [
      { value: true, label: 'Oui' },
      { value: false, label: 'Non' },
    ];

    const questionOptions = ref<Array<{ value: number; label: string }>>([]);

    // Chargement des questions
    const loadQuestions = async () => {
      loadingQuestions.value = true;
      try {
        const { data } = await ApiService.get('/questions');
        questionOptions.value = data.data.data.map((q: any) => ({
          value: q.id,
          label: q.contenu.length > 80 ? q.contenu.substring(0, 80) + '...' : q.contenu,
        }));
      } catch (err) {
        error('Erreur lors du chargement des questions');
      } finally {
        loadingQuestions.value = false;
      }
    };

    // Chargement de la proposition + pré-remplissage
    const loadProposition = async () => {
      try {
        const { data } = await ApiService.get(`/propositions/${id}`);
        const proposition = data.data;

        if (!proposition) {
          error('Proposition non trouvée');
          return;
        }

        console.log('Proposition chargée →', proposition);

        await nextTick();

        // Récupération de l'ID de la question
        const questionId = proposition.question?.id || proposition.questionId;

        // Pré-remplissage du formulaire avec UNIQUEMENT LES VALEURS (pas les objets)
        propositionForm.value?.setValues({
          contenu: proposition.contenu,
          estLaBonneReponse: proposition.estLaBonneReponse,
          questionId: questionId, // UNIQUEMENT L'ID NUMÉRIQUE
        });

        // Force la mise à jour du Multiselect (important !)
        await nextTick();
        propositionForm.value?.setFieldValue('questionId', questionId);

        console.log('Formulaire pré-rempli avec questionId:', questionId);
      } catch (err: any) {
        console.error(err);
        error('Impossible de charger la proposition');
      }
    };

    // Modification
    const editProposition = async () => {
      if (!propositionForm.value) return;

      isSubmitting.value = true;

      try {
        const values = propositionForm.value.getValues();

        const payload = {
          contenu: values.contenu,
          estLaBonneReponse: values.estLaBonneReponse,
          questionId: values.questionId, // C'est bien un nombre
        };

        await ApiService.put(`/propositions/${id}`, payload);

        Swal.fire({
          icon: 'success',
          title: 'Succès !',
          text: 'Proposition modifiée avec succès',
          timer: 1800,
          showConfirmButton: false,
        });

        router.push('/liste-propositions');
      } catch (err: any) {
        Swal.fire({
          icon: 'error',
          title: 'Échec',
          text: err.response?.data?.message || 'Erreur lors de la modification',
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    onMounted(async () => {
      await loadQuestions();
      await loadProposition();
    });

    return {
      propositionForm,
      propositionSchema,
      booleanOptions,
      questionOptions,
      editProposition,
      isSubmitting,
      loadingQuestions,
    };
  },
});
</script>

<style src="@vueform/multiselect/themes/default.css"></style>